import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { GetUngatedPaginatedShopCategoryQuery } from './getUngatedPaginatedShopCategory';
import { getShopProductSourceForType, type ShopProductSourceType } from './getShopProductSource';
export type ShopCategoryGridPaginationProps = {
  gridId?: string;
  previousGridId?: string;
  showMoreEnabled?: boolean;
};
type ShopCategoryPaginationCursor = {
  sourceId: string;
  sourceType: ShopProductSourceType;
  cursor?: string | null;
};
type ShopCategoryGridCursorContextValue = {
  cursorsByGridId: Record<string, ShopCategoryPaginationCursor>;
  setCursorForGrid: (gridId: string, sourceId: string, sourceType: ShopProductSourceType, cursor: string | null) => void;
};
const ShopCategoryGridCursorContext = createContext<ShopCategoryGridCursorContextValue | null>(null);
export const ShopCategoryGridCursorProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  // track each grid's continuation cursor so later grids for the same source can continue from it.
  const [cursorsByGridId, setCursorsByGridId] = useState<Record<string, ShopCategoryPaginationCursor>>({});
  const setCursorForGrid = useCallback((gridId: string, sourceId: string, sourceType: ShopProductSourceType, cursor: string | null) => {
    setCursorsByGridId(currentCursors => {
      const existingCursor = currentCursors[gridId];
      if (existingCursor?.sourceId === sourceId && existingCursor.sourceType === sourceType && existingCursor.cursor === cursor) {
        return currentCursors;
      }

      // merge cursor updates because grids can complete in separate render passes.
      return {
        ...currentCursors,
        [gridId]: {
          sourceId,
          sourceType,
          cursor
        }
      };
    });
  }, []);
  return <ShopCategoryGridCursorContext.Provider value={{
    cursorsByGridId,
    setCursorForGrid
  }}>
      {children}
    </ShopCategoryGridCursorContext.Provider>;
};
const getShopProductsContinuationCursor = (data: GetUngatedPaginatedShopCategoryQuery | undefined, sourceType: ShopProductSourceType) => {
  const pageInfo = getShopProductSourceForType(data, sourceType)?.connection.pageInfo;
  return pageInfo?.hasNextPage ? pageInfo.endCursor ?? null : null;
};
export const useShopCategoryGridPagination = ({
  sourceId,
  sourceType,
  gridId,
  previousGridId
}: Pick<ShopCategoryGridPaginationProps, 'gridId' | 'previousGridId'> & {
  sourceId: string;
  sourceType: ShopProductSourceType;
}) => {
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const {
    cursorsByGridId = {},
    setCursorForGrid
  } = useContext(ShopCategoryGridCursorContext) ?? {};
  const previousGridCursor = previousGridId ? cursorsByGridId[previousGridId] : undefined;
  const currentGridCursor = gridId ? cursorsByGridId[gridId] : undefined;
  const isPreviousCursorForSameSource = previousGridCursor?.sourceId === sourceId && previousGridCursor.sourceType === sourceType;
  const paginationCursor = isPreviousCursorForSameSource ? previousGridCursor?.cursor : undefined;

  // keep this grid in a placeholder state until the previous grid publishes any cursor result.
  const isWaitingForPreviousGridCursor = Boolean(previousGridId && !previousGridCursor);

  // a null cursor from the previous grid means the source has no more
  // products, so this grid has nothing left to load.
  const hasNoMorePreviousGridProducts = isPreviousCursorForSameSource && paginationCursor === null;
  const setCursorFromData = useCallback((nextData?: GetUngatedPaginatedShopCategoryQuery) => {
    if (!gridId || !setCursorForGrid) {
      return;
    }
    setCursorForGrid(gridId, sourceId, sourceType, getShopProductsContinuationCursor(nextData, sourceType));
  }, [sourceId, sourceType, gridId, setCursorForGrid]);
  useEffect(() => {
    // when the previous grid has no more products, propagate its exhausted state.
    if (!gridId || !setCursorForGrid || !hasNoMorePreviousGridProducts || currentGridCursor?.sourceId === sourceId && currentGridCursor.sourceType === sourceType) {
      return;
    }
    setCursorForGrid(gridId, sourceId, sourceType, null);
  }, [sourceId, sourceType, currentGridCursor?.sourceId, currentGridCursor?.sourceType, gridId, hasNoMorePreviousGridProducts, setCursorForGrid]);
  return {
    paginationCursor,
    hasNoMorePreviousGridProducts,
    isLoadingMore,
    isWaitingForPreviousGridCursor,
    setCursorFromData,
    setIsLoadingMore
  };
};